import React from 'react';
import { CheckCircle2, ExternalLink, Loader2, ShieldAlert, Wand2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { KIND_LABELS, KIND_COLORS } from './parts';
import { keyFor } from './useLinkHealth';

const SuggestionPreview = ({ b, sug, onApply, onDismiss }) => (
  <div
    className={`mt-3 ml-16 rounded-lg border p-3 ${
      sug.verified ? 'border-emerald-300 bg-emerald-50/50' : 'border-[#D4AF37]/40 bg-[#FDFBF7]/50'
    }`}
    data-testid={`suggestion-preview-${b.unit_id || 'na'}`}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          {sug.verified
            ? <Badge className="bg-emerald-200 text-emerald-900 text-[11px]"><CheckCircle2 className="h-3 w-3 mr-1" />Verified {sug.validation?.status}</Badge>
            : <Badge className="bg-[#F5EBC0] text-[#14213D] text-[11px]"><ShieldAlert className="h-3 w-3 mr-1" />Unverified — {sug.validation?.status || '?'}</Badge>}
          <span className="text-xs text-[#5A6577]">{sug.suggestion.source_organization}</span>
        </div>
        <a
          href={sug.suggestion.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-[#14213D] hover:text-[#2D3748] inline-flex items-center gap-1 break-all font-medium"
          data-testid={`suggestion-url-${b.unit_id || 'na'}`}
        >
          {sug.suggestion.url}
          <ExternalLink className="h-3 w-3 shrink-0" />
        </a>
        {sug.suggestion.rationale && (
          <p className="text-xs text-[#5A6577] mt-1 italic">&ldquo;{sug.suggestion.rationale}&rdquo;</p>
        )}
      </div>
      <div className="flex flex-col gap-1 shrink-0">
        <Button
          size="sm"
          className="btn-primary text-xs px-3 py-1 h-7"
          onClick={() => onApply(b)}
          data-testid={`apply-fix-btn-${b.unit_id || 'na'}`}
        >
          Apply
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="text-xs px-3 py-1 h-7 text-[#5A6577] hover:text-[#2D3748]"
          onClick={() => onDismiss(b)}
          data-testid={`dismiss-suggestion-${b.unit_id || 'na'}`}
        >
          <X className="h-3 w-3 mr-1" />Skip
        </Button>
      </div>
    </div>
  </div>
);

const BrokenRow = ({ b, sug, onSuggest, onApply, onDismiss }) => (
  <div className="px-5 py-3 hover:bg-[#F9F8F4]/50">
    <div className="flex items-start gap-4">
      <Badge className={`${KIND_COLORS[b.kind] || 'bg-[#F9F8F4]'} text-[11px] mt-0.5 shrink-0`}>
        {KIND_LABELS[b.kind] || b.kind}
      </Badge>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-[#14213D] truncate">{b.source_title}</div>
        <a
          href={b.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-[#5A6577] hover:text-[#2D3748] inline-flex items-center gap-1 break-all"
        >
          {b.url}
          <ExternalLink className="h-3 w-3 shrink-0" />
        </a>
      </div>
      <div className="text-xs font-mono text-red-700 shrink-0 mt-1" data-testid="broken-status">
        {b.status || b.error || 'err'}
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={() => onSuggest(b)}
        disabled={sug?.loading}
        className="shrink-0 border-yellow-500 text-[#14213D] hover:bg-[#FBF6E8]"
        data-testid={`fix-with-ai-btn-${b.unit_id || 'na'}`}
      >
        {sug?.loading
          ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Searching…</>
          : <><Wand2 className="h-3 w-3 mr-1" />Fix with AI</>
        }
      </Button>
    </div>

    {sug?.suggestion?.url && !sug.applied && (
      <SuggestionPreview b={b} sug={sug} onApply={onApply} onDismiss={onDismiss} />
    )}

    {sug && !sug.loading && !sug.suggestion?.url && !sug.applied && (
      <div className="mt-2 ml-16 text-xs text-red-700">
        {sug.error || 'AI could not find a replacement. Try a manual search.'}
      </div>
    )}

    {sug?.applied && (
      <div className="mt-2 ml-16 text-xs text-emerald-700 flex items-center gap-1">
        <CheckCircle2 className="h-3 w-3" /> Applied
      </div>
    )}
  </div>
);

export const BrokenUnitGroup = ({ unitTitle, items, suggestions, onSuggest, onApply, onDismiss }) => (
  <Card className="overflow-hidden" data-testid={`unit-group-${unitTitle.slice(0, 30).replace(/\s+/g, '-')}`}>
    <div className="bg-[#F9F8F4] px-5 py-3 border-b border-[#EBE7E0] flex items-center justify-between">
      <h4 className="font-serif text-[#14213D]">{unitTitle}</h4>
      <Badge variant="outline" className="text-xs">{items.length} broken</Badge>
    </div>
    <div className="divide-y divide-stone-100">
      {items.map((b) => (
        <BrokenRow
          key={`${b.url}-${b.source_title}`}
          b={b}
          sug={suggestions[keyFor(b)]}
          onSuggest={onSuggest}
          onApply={onApply}
          onDismiss={onDismiss}
        />
      ))}
    </div>
  </Card>
);
